import React, { useRef, useEffect, useState } from 'react';

const WebcamCapture = ({ isRecording, conversationId }) => {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const captureIntervalRef = useRef(null);
  const initialCaptureDoneRef = useRef(false);
  const [debugInfo, setDebugInfo] = useState('');
  
  // New refs for composite image creation
  const compositeCanvasRef = useRef(null);
  const capturedFramesRef = useRef([]);
  const maxFramesRef = useRef(9); // 3x3 grid

  useEffect(() => {
    if (isRecording) {
      startWebcam();
    } else {
      stopWebcam();
    }
    return () => {
      stopWebcam();
    };
  }, [isRecording]);

  const startWebcam = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: true });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.onloadedmetadata = () => {
          setDebugInfo('Video metadata loaded');
          const playPromise = videoRef.current.play();
          if (playPromise) {
            playPromise.catch((error) => {
              setDebugInfo(`Camera preview could not play: ${error.message}`);
            });
          }
          
          // Reset for new recording session
          initialCaptureDoneRef.current = false;
          capturedFramesRef.current = [];
          
          // Initialize composite canvas
          const video = videoRef.current;
          if (!compositeCanvasRef.current) {
            compositeCanvasRef.current = document.createElement('canvas');
            compositeCanvasRef.current.width = video.videoWidth * 3;
            compositeCanvasRef.current.height = video.videoHeight * 3;
          }
          
          // Capture first frame immediately after video stabilizes
          setTimeout(() => {
            captureFrameToComposite();
            startFrameCapture();
          }, 500);
        };
      } else {
        setDebugInfo('Video reference is not available');
      }
    } catch (error) {
      setDebugInfo(`Error accessing webcam: ${error.message}`);
    }
  };

  const stopWebcam = () => {
    // Send composite image for analysis before stopping
    if (capturedFramesRef.current.length > 0 && !isRecording) {
      sendCompositeForAnalysis();
    }
    
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
    }
    if (captureIntervalRef.current) {
      clearInterval(captureIntervalRef.current);
    }
    setDebugInfo('Webcam stopped');
  };

  const startFrameCapture = () => {
    // Capture frames every 2 seconds instead of 3
    captureIntervalRef.current = setInterval(() => {
      if (capturedFramesRef.current.length < maxFramesRef.current) {
        captureFrameToComposite();
      }
    }, 2000);
  };

  const captureFrameToComposite = () => {
    if (videoRef.current && canvasRef.current && compositeCanvasRef.current) {
      const video = videoRef.current;
      const canvas = canvasRef.current;
      const ctx = canvas.getContext('2d');
      
      // Set canvas to video dimensions
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
      
      // Check if frame is valid (not black)
      const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
      const isBlack = imageData.data.every((value, index) => index % 4 !== 3 && value === 0);
      
      if (!isBlack) {
        // Add frame to composite
        const frameIndex = capturedFramesRef.current.length;
        const col = frameIndex % 3;
        const row = Math.floor(frameIndex / 3);
        
        const compositeCtx = compositeCanvasRef.current.getContext('2d');
        compositeCtx.drawImage(
          canvas,
          col * video.videoWidth,
          row * video.videoHeight,
          video.videoWidth,
          video.videoHeight
        );
        
        capturedFramesRef.current.push({
          timestamp: Date.now(),
          index: frameIndex
        });
        
        setDebugInfo(`Captured frame ${frameIndex + 1}/9 at position (${col}, ${row})`);
      } else {
        setDebugInfo('Frame is black, skipping');
      }
    }
  };

  const sendCompositeForAnalysis = async () => {
    if (!compositeCanvasRef.current || capturedFramesRef.current.length === 0) {
      return;
    }
    
    try {
      // Convert composite canvas to base64
      const compositeData = compositeCanvasRef.current.toDataURL('image/jpeg', 0.8);
      const base64data = compositeData.split(',')[1];
      
      const formData = new FormData();
      formData.append('composite_frame', base64data);
      formData.append('conversation_id', conversationId);
      formData.append('frame_count', capturedFramesRef.current.length);
      formData.append('frame_timestamps', JSON.stringify(capturedFramesRef.current));
      
      setDebugInfo(`Sending composite with ${capturedFramesRef.current.length} frames for analysis`);
      
      const response = await fetch('https://nump0.com/persona/analyze_composite_frames', {
        method: 'POST',
        body: formData,
        credentials: 'include',
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const responseData = await response.json();
      setDebugInfo(`Analysis complete: ${responseData.status}`);
      
      // Clear the composite for next recording session
      const compositeCtx = compositeCanvasRef.current.getContext('2d');
      compositeCtx.clearRect(0, 0, compositeCanvasRef.current.width, compositeCanvasRef.current.height);
      capturedFramesRef.current = [];
      
    } catch (error) {
      setDebugInfo(`Error sending composite: ${error.message}`);
      console.error('Error sending composite for analysis:', error);
    }
  };
  
  return (
      <div style={{ display: isRecording ? 'block' : 'none', position: 'relative' }}>
        <video
          ref={videoRef}
          style={{
            width: '40px',
            height: '30px',
            borderRadius: '10px',
          }}
          autoPlay
          playsInline
          muted
        />
        <canvas
          ref={canvasRef}
          style={{
            display: 'none',
            border: '1px solid white',
            borderRadius: '10px',
          }}
        />
        <div style={{
          position: 'absolute',
          top: '100%',
          left: 0,
          backgroundColor: 'rgba(0,0,0,0.7)',
          color: 'white',
          padding: '5px',
          fontSize: '12px',
          whiteSpace: 'pre-wrap',
          maxWidth: '320px',
          wordBreak: 'break-word',
          borderRadius: '10px',
        }}>
          {/* Debug Info: {debugInfo} */}
        </div>
      </div>
    );
}

export default WebcamCapture;
